import type { UseFormReturn } from 'react-hook-form';

import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import type { CalculationData } from '~/api/common/types';

import { useClients } from '~/api/clients';
import { useUser } from '~/api/user';
import { ExternalLink } from '~/components';
import { clsx } from '~/styles/utils';

import { getVatRegime } from '../documents.utils';
import { PriceSummary } from '../PriceSummary/PriceSummary';
import { loadingStyle, wrapperStyle } from './LinesTotal.css';

type FormWithClient = {
  clientId: number | null;
};

type Props<T extends FormWithClient> = {
  calculationData: CalculationData;
  form: T extends FormWithClient ? UseFormReturn<T> : never;
  isLoading: boolean;
};

export const LinesTotal = <T extends FormWithClient>({ calculationData, form: { watch }, isLoading }: Props<T>) => {
  const { data: clients } = useClients();
  const { data: user } = useUser();

  const client = clients.find(({ id }) => id === watch('clientId')) ?? null;
  const vatRegime = useMemo(() => getVatRegime(client), [client]);

  return (
    <div className={clsx(wrapperStyle, isLoading && loadingStyle)} data-testid="lines-total">
      <PriceSummary
        calculationData={calculationData}
        vatExemptionReason={client?.exceptionalZeroVatReason ?? undefined}
        vatRegime={vatRegime}
        vatTooltip={user.vatExempt && <Trans components={{ a: <ExternalLink /> }} i18nKey="lines:vatTooltip" />}
      />
    </div>
  );
};
